"use strict";
/**
 * Slug Generation Utilities
 *
 * Purpose: Generate URL-friendly slugs from merchant names for customer registration links
 *
 * Story: 3.6 - Merchant Registration Link Generation (MVP)
 *
 * Rules:
 * - Lowercase only
 * - Spaces → hyphens
 * - Remove special characters (keep alphanumeric and hyphens)
 * - No consecutive hyphens
 * - No leading/trailing hyphens
 * - 3-50 characters
 *
 * Examples:
 * - "Cafe Luna" → "cafe-luna"
 * - "Bella's Spa & Wellness" → "bellas-spa-wellness"
 * - "Café München" → "cafe-munchen"
 * - "  Extra   Spaces  " → "extra-spaces"
 * - "123 Numbers!" → "123-numbers"
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.generateSlug = generateSlug;
exports.generateUniqueSlug = generateUniqueSlug;
exports.isValidSlug = isValidSlug;
/**
 * Generate a URL-friendly slug from a string
 *
 * @param name - The name to convert to a slug
 * @returns URL-friendly slug (3-50 characters)
 *
 * @example
 * generateSlug("Cafe Luna") // => "cafe-luna"
 * generateSlug("Bella's Spa & Wellness") // => "bellas-spa-wellness"
 */
function generateSlug(name) {
    return name
        .toLowerCase()
        .normalize('NFD') // Decompose accented chars (é → e + ´)
        .replace(/[\u0300-\u036f]/g, '') // Remove accent marks
        .replace(/[^a-z0-9\s-]/g, '') // Remove special chars
        .replace(/\s+/g, '-') // Spaces → hyphens
        .replace(/-+/g, '-') // Collapse multiple hyphens
        .replace(/^-|-$/g, '') // Trim hyphens from ends
        .slice(0, 50); // Max 50 chars
}
/**
 * Generate a unique slug by appending a number if needed
 *
 * @param baseSlug - The base slug to make unique
 * @param existingSlugs - Array of existing slugs to check against
 * @returns Unique slug (appends -2, -3, etc. if needed)
 *
 * @example
 * generateUniqueSlug("cafe-luna", ["cafe-luna"]) // => "cafe-luna-2"
 * generateUniqueSlug("cafe-luna", ["cafe-luna", "cafe-luna-2"]) // => "cafe-luna-3"
 * generateUniqueSlug("new-cafe", []) // => "new-cafe"
 */
function generateUniqueSlug(baseSlug, existingSlugs) {
    if (!existingSlugs.includes(baseSlug)) {
        return baseSlug;
    }
    let counter = 2;
    let newSlug = `${baseSlug}-${counter}`;
    while (existingSlugs.includes(newSlug)) {
        counter++;
        newSlug = `${baseSlug}-${counter}`;
    }
    return newSlug;
}
/**
 * Validate if a string is a valid slug format
 *
 * @param slug - The slug to validate
 * @returns true if valid, false otherwise
 *
 * Rules:
 * - 3-50 characters
 * - Lowercase alphanumeric and hyphens only
 * - Cannot start or end with a hyphen
 * - No consecutive hyphens
 *
 * @example
 * isValidSlug("cafe-luna") // => true
 * isValidSlug("Cafe Luna") // => false (uppercase)
 * isValidSlug("ab") // => false (too short)
 * isValidSlug("cafe_luna") // => false (underscore not allowed)
 * isValidSlug("-cafe") // => false (starts with hyphen)
 * isValidSlug("cafe-") // => false (ends with hyphen)
 */
function isValidSlug(slug) {
    // Must be 3-50 chars, lowercase alphanumeric and hyphens only
    // Cannot start or end with hyphen, no consecutive hyphens
    if (slug.length < 3 || slug.length > 50)
        return false;
    if (slug.startsWith('-') || slug.endsWith('-'))
        return false;
    if (slug.includes('--'))
        return false;
    return /^[a-z0-9-]+$/.test(slug);
}
